import { useCallback, useEffect, useState } from "react";
import { api, errDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Check } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useTheme, THEMES } from "../context/ThemeContext";
import ExchangeSection, { EXCHANGE_META } from "../components/ExchangeSection";
import { AdminUsers } from "../components/AdminUsers";
import { LOCALES, setLocaleStorage } from "../lib/format";

export default function Settings() {
  const { config, user, setUser } = useAuth();
  const { theme, setTheme } = useTheme();
  const [exchanges, setExchanges] = useState([]);
  const [tpInterp, setTpInterp] = useState(user?.settings?.tp_interpretation || "price_move");
  const [defaultEx, setDefaultEx] = useState(user?.settings?.default_exchange || "bybit");
  const [syncEnabled, setSyncEnabled] = useState(user?.settings?.sync_enabled || false);
  const [syncInterval, setSyncInterval] = useState(user?.settings?.sync_interval_min || 1);
  const [autoImport, setAutoImport] = useState(user?.settings?.auto_import_enabled || false);
  const [syncing, setSyncing] = useState(false);
  const [locale, setLocale] = useState(user?.settings?.locale || "en-US");
  const [setupTags, setSetupTags] = useState(user?.settings?.setup_tags || []);
  const [newTag, setNewTag] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);

  const persistTags = async (tags) => {
    setSetupTags(tags);
    try {
      const { data } = await api.put("/settings", { setup_tags: tags });
      setUser({ ...user, settings: data });
    } catch (e) {
      toast.error(errDetail(e));
    }
  };
  const addSetupTag = () => {
    const t = newTag.trim().toLowerCase().replace(/\s+/g, "-");
    if (!t || setupTags.includes(t)) { setNewTag(""); return; }
    setNewTag("");
    persistTags([...setupTags, t]);
  };
  const removeSetupTag = (t) => persistTags(setupTags.filter((x) => x !== t));

  const saveLocale = async (v) => {
    setLocale(v);
    setLocaleStorage(v);
    try {
      const { data } = await api.put("/settings", { locale: v });
      setUser({ ...user, settings: data });
      toast.success(`Number & date format set to ${v} (e.g. ${(1216.67).toLocaleString(v, { minimumFractionDigits: 2 })})`);
    } catch (e) {
      toast.error(errDetail(e));
    }
  };

  const load = useCallback(async () => {
    const { data } = await api.get("/exchanges");
    setExchanges(data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveDefaultEx = async (v) => {
    setDefaultEx(v);
    try {
      const { data } = await api.put("/settings", { default_exchange: v });
      setUser({ ...user, settings: data });
      toast.success(`Default exchange set to ${EXCHANGE_META[v].label}`);
    } catch (e) {
      toast.error(errDetail(e));
    }
  };

  const saveSyncEnabled = async (v) => {
    setSyncEnabled(v);
    try {
      const { data } = await api.put("/settings", { sync_enabled: v });
      setUser({ ...user, settings: data });
      toast.success(v ? "Exchange sync enabled" : "Exchange sync disabled");
    } catch (e) {
      toast.error(errDetail(e));
    }
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/exchanges/sync");
      const parts = [];
      if (data.filled) parts.push(`${data.filled} filled`);
      if (data.closed) parts.push(`${data.closed} closed`);
      if (data.cancelled) parts.push(`${data.cancelled} cancelled`);
      if (data.imported) parts.push(`${data.imported} imported`);
      toast.success(parts.length ? `Synced — ${parts.join(", ")}` : "Synced — nothing to update");
      if (data.errors?.length) toast.error(data.errors.join("; "));
    } catch (e) {
      toast.error(errDetail(e));
    } finally {
      setSyncing(false);
    }
  };

  const saveAutoImport = async (v) => {
    setAutoImport(v);
    try {
      const { data } = await api.put("/settings", { auto_import_enabled: v });
      setUser({ ...user, settings: data });
      toast.success(v ? "Auto-import of new exchange trades enabled" : "Auto-import disabled");
    } catch (e) {
      toast.error(errDetail(e));
    }
  };

  const saveSyncInterval = async () => {
    const v = Math.max(1, Math.round(parseFloat(syncInterval) || 1));
    setSyncInterval(v);
    try {
      const { data } = await api.put("/settings", { sync_interval_min: v });
      setUser({ ...user, settings: data });
      toast.success(`Sync interval set to ${v} minute(s)`);
    } catch (e) {
      toast.error(errDetail(e));
    }
  };

  const saveTpInterp = async (v) => {
    setTpInterp(v);
    try {
      const { data } = await api.put("/settings", { tp_interpretation: v });
      setUser({ ...user, settings: data });
      toast.success("TP behavior updated");
    } catch (e) {
      toast.error(errDetail(e));
    }
  };

  const changePassword = async () => {
    if (!currentPassword || !newPassword) {
      toast.error("Current and new password are required");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("New password and confirmation don't match");
      return;
    }
    setChangingPassword(true);
    try {
      await api.put("/auth/password", { current_password: currentPassword, new_password: newPassword });
      toast.success("Password changed");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (e) {
      toast.error(errDetail(e));
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <div className="max-w-3xl space-y-8" data-testid="settings-page">
      <h1 className="font-heading font-bold text-3xl tracking-tight">Settings</h1>

      {config?.app_mode === "multi" && user?.is_admin && <AdminUsers />}

      {config?.app_mode === "multi" && (
        <section className="border border-border bg-card">
          <div className="px-5 py-3 border-b border-border label-caps text-muted-foreground">Change Password</div>
          <div className="p-5 space-y-3 max-w-sm">
            {user?.is_admin && (
              <p className="text-xs text-muted-foreground">
                First time changing it? Use the initial password from ADMIN_PASSWORD as your current password.
              </p>
            )}
            <div>
              <Label className="label-caps text-muted-foreground">Current Password</Label>
              <Input data-testid="current-password-input" type="password" className="mt-1"
                value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} />
            </div>
            <div>
              <Label className="label-caps text-muted-foreground">New Password</Label>
              <Input data-testid="new-password-input" type="password" className="mt-1"
                value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
            </div>
            <div>
              <Label className="label-caps text-muted-foreground">Confirm New Password</Label>
              <Input data-testid="confirm-password-input" type="password" className="mt-1"
                value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
            </div>
            <Button data-testid="change-password-btn" onClick={changePassword} disabled={changingPassword}
              className="bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground">
              {changingPassword ? "Changing…" : "Change Password"}
            </Button>
          </div>
        </section>
      )}

      <section className="border border-border bg-card">
        <div className="px-5 py-3 border-b border-border label-caps text-muted-foreground">Appearance</div>
        <div className="p-5">
          <Label className="label-caps text-muted-foreground">Color theme</Label>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-3" data-testid="theme-picker">
            {THEMES.map((t) => (
              <button
                key={t.id}
                data-testid={`settings-theme-${t.id}`}
                onClick={() => setTheme(t.id)}
                className={`border p-3 flex flex-col items-start gap-2 transition-colors duration-150 ${
                  theme === t.id ? "border-[hsl(var(--primary))]" : "border-border hover:border-[hsl(var(--muted-foreground))]"
                }`}
              >
                <span className="w-full h-8 rounded-sm border border-border relative overflow-hidden" style={{ background: t.swatch[1] }}>
                  <span className="absolute left-2 top-2 w-10 h-1.5 rounded-full" style={{ background: t.swatch[0] }} />
                  <span className="absolute left-2 bottom-1.5 w-6 h-1 rounded-full opacity-40" style={{ background: t.swatch[0] }} />
                </span>
                <span className="flex items-center gap-1.5 text-sm">
                  {t.label}
                  {theme === t.id && <Check size={13} className="text-[hsl(var(--primary))]" weight="bold" />}
                </span>
              </button>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-3">Also switchable anytime from the palette icon in the top bar. Saved to your profile.</p>
        </div>
      </section>

      {Object.keys(EXCHANGE_META).map((ex) => (
        <ExchangeSection
          key={ex}
          exchange={ex}
          cfg={exchanges.find((e) => e.exchange === ex)}
          globalLive={config?.trading_mode === "live"}
          onChanged={load}
        />
      ))}

      <section className="border border-border bg-card">
        <div className="px-5 py-3 border-b border-border label-caps text-muted-foreground">Trading Preferences</div>
        <div className="p-5 space-y-6">
          <div className="space-y-2">
            <Label className="label-caps text-muted-foreground">Number &amp; date format (locale)</Label>
            <Select value={locale} onValueChange={saveLocale}>
              <SelectTrigger data-testid="locale-select" className="w-72 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {LOCALES.map((l) => (
                  <SelectItem key={l.id} value={l.id}>{l.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Controls how prices, P/L and dates are displayed everywhere in the app (e.g. Nederlands: 1.216,67 instead of 1,216.67).
            </p>
          </div>
          <div className="space-y-2">
            <Label className="label-caps text-muted-foreground">Setup tags (used in the trade form &amp; Analytics)</Label>
            <div className="flex flex-wrap gap-2" data-testid="settings-setup-tags">
              {setupTags.map((t) => (
                <span key={t} className="flex items-center gap-1.5 text-xs font-mono uppercase border border-border px-2 py-1">
                  {t}
                  <button data-testid={`remove-tag-${t}`} onClick={() => removeSetupTag(t)}
                    className="text-muted-foreground hover:text-[hsl(var(--loss))] transition-colors duration-150">×</button>
                </span>
              ))}
              <Input data-testid="settings-new-tag-input" value={newTag} placeholder="add tag + Enter"
                className="h-7 w-40 text-xs font-mono"
                onChange={(e) => setNewTag(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") addSetupTag(); }} />
            </div>
            <p className="text-xs text-muted-foreground">
              Removing a tag here only removes it from the pick list — trades keep their tags.
            </p>
          </div>
          <div className="space-y-2">
            <Label className="label-caps text-muted-foreground">Default exchange (for "Send to exchange" on new trades)</Label>
            <Select value={defaultEx} onValueChange={saveDefaultEx}>
              <SelectTrigger data-testid="default-exchange-select" className="w-72 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.keys(EXCHANGE_META).map((ex) => (
                  <SelectItem key={ex} value={ex}>
                    {EXCHANGE_META[ex].label}
                    {!exchanges.find((e) => e.exchange === ex) ? " — not configured" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              New trades can optionally be sent to this exchange. It must be configured and set to live for orders to go out.
            </p>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div>
                <Label className="label-caps text-muted-foreground">Exchange sync (detect trades closed on the exchange)</Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Periodically checks your exchanges and auto-closes journal trades that were closed there (with the real exit price and P/L). Disabled by default.
                </p>
              </div>
              <Switch data-testid="sync-enabled-switch" checked={syncEnabled} onCheckedChange={saveSyncEnabled} />
            </div>
            <Button data-testid="sync-now-btn" variant="outline" size="sm" onClick={syncNow} disabled={syncing}>
              {syncing ? "Syncing…" : "Sync Now"}
            </Button>
            {syncEnabled && (
              <div className="flex items-center gap-2">
                <Label className="text-sm text-muted-foreground">Poll every</Label>
                <Input data-testid="sync-interval-input" type="number" min="1" step="1" className="w-24 font-mono"
                  value={syncInterval} onChange={(e) => setSyncInterval(e.target.value)} onBlur={saveSyncInterval} />
                <span className="text-sm text-muted-foreground">minute(s) — minimum 1</span>
              </div>
            )}
            {syncEnabled && (
              <div className="flex items-center justify-between pt-2">
                <div>
                  <Label className="label-caps text-muted-foreground">Auto-import new exchange trades</Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    New positions you open directly on the exchange are automatically added to your journal (with a notification). Without this, use the Import button per exchange.
                  </p>
                </div>
                <Switch data-testid="auto-import-switch" checked={autoImport} onCheckedChange={saveAutoImport} />
              </div>
            )}
          </div>
          <div className="space-y-2">
            <Label className="label-caps text-muted-foreground">Interpret TP percentage as</Label>
            <Select value={tpInterp} onValueChange={saveTpInterp}>
              <SelectTrigger data-testid="tp-interpretation-select" className="w-72 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="price_move">Price-move % (target from entry)</SelectItem>
                <SelectItem value="position_size">Position-size % (portion to close)</SelectItem>
                <SelectItem value="both">Both (price move + close size)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Price-move %: TP target auto-calculated from entry. Position-size %: the close-size field controls how much of the position closes at each TP.
            </p>
          </div>
        </div>
      </section>

      <section className="border border-border bg-card">
        <div className="px-5 py-3 border-b border-border label-caps text-muted-foreground">System</div>
        <div className="p-5 grid grid-cols-3 gap-4 text-sm font-mono" data-testid="system-config-panel">
          <div>
            <div className="label-caps text-muted-foreground mb-1">App Mode</div>
            <div>{config?.app_mode}</div>
          </div>
          <div>
            <div className="label-caps text-muted-foreground mb-1">Global Trading</div>
            <div className={config?.trading_mode === "live" ? "text-profit" : ""}>{config?.trading_mode}</div>
          </div>
          <div>
            <div className="label-caps text-muted-foreground mb-1">Photo Storage</div>
            <div>{config?.photo_storage}</div>
          </div>
          <p className="col-span-3 text-xs text-muted-foreground font-sans">
            These are set via environment variables in backend/.env: APP_MODE (single/multi), TRADING_MODE (readonly/live), PHOTO_STORAGE (local/object).
          </p>
        </div>
      </section>
    </div>
  );
}
